import { Button } from '@/components/common/Button';
import { ROUTES } from '@/configs/constants';
import { useStore } from '@/hooks/useStore';
import { SHOP_STATUS_TYPE } from '@/types/enums';
import { GetGeneralInfoResponse } from '@/types/http-payload/shop';
import { BanIcon, CheckIcon, CircleCheckIcon, XIcon } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';
import ConfirmModalWithReason from '../modals/ConfirmModalWithReason';
import { flowResult } from 'mobx';
import { toastify } from '@/utils/toastify';
import { useTranslation } from 'react-i18next';
import { UseFormReturn } from 'react-hook-form';
import Tooltip from '@/components/common/Tooltip';
import { ReactElement } from 'react';
import classNames from 'classnames';

interface EditShopFormFooterProps {
    code: string,
    onSubmit: () => void,
    isSubmitDisabled: boolean,
    form: UseFormReturn<any>,
    fetchData: Function
}

export default observer(function EditShopFormFooter({ code, onSubmit, isSubmitDisabled, form, fetchData }: EditShopFormFooterProps) {
    // hooks
    const navigate = useNavigate();
    const { t } = useTranslation();

    // store
    const {
        shopStore: { shopInfo, updateStatus },
        modalStore: { showAlertModal, modalWaringAlertOptions }
    } = useStore();

    // function
    const handleClickCancel = () => {
        navigate(ROUTES.shopManagement.href);
    };

    const handleClickAccept = async () => {
        showAlertModal(modalWaringAlertOptions({
            hideIconCenter: true,
            content: t('messages.confirm_accept_shop'),
            onSave: async () => {
                const res = await flowResult(updateStatus(code, { status: SHOP_STATUS_TYPE.Active }));
                if (res && res.ok) {
                    fetchData();
                    toastify('success', t('messages.update_success'));
                }
            }
        }));
    };

    const handleClickDenied = () => {
        showAlertModal(modalWaringAlertOptions({
            size: 'xl',
            hideIconCenter: true,
            contentClassName: 'text-start',
            content: <ConfirmModalWithReason text={t('messages.confirm_denied_shop')} submitFunction={handleSubmitDeniedModal} />,
            footerClassName: 'p-0',
            footer: <></>
        }));
    };

    const handleSubmitDeniedModal = async ({ reason }, cb) => {
        const res = await flowResult(updateStatus(code, {
            status: SHOP_STATUS_TYPE.DeniedApprove,
            reason
        }));
        if (res?.ok) {
            cb && cb();
            fetchData();
            toastify('success', t('messages.update_success'));
        }
    };

    const handleClickActive = async () => {
        showAlertModal(modalWaringAlertOptions({
            hideIconCenter: true,
            content: shopInfo?.status === SHOP_STATUS_TYPE.NotCompletedInfo ? t('messages.confirm_active_shop_with_not_complete') : t('messages.confirm_active_shop'),
            onSave: async () => {
                const res = await flowResult(updateStatus(code, { status: shopInfo?.status === SHOP_STATUS_TYPE.NotCompletedInfo ? SHOP_STATUS_TYPE.Active : SHOP_STATUS_TYPE.WaitApprove }));
                if (res && res.ok) {
                    fetchData();
                    toastify('success', t('messages.update_success'));
                }
            }
        }));
    };

    const handleClickInactive = () => {
        showAlertModal(modalWaringAlertOptions({
            size: 'xl',
            hideIconCenter: true,
            contentClassName: 'text-start',
            content: <ConfirmModalWithReason text={t('messages.confirm_inactive_shop')} submitFunction={handleSubmitInactiveModal} />,
            footerClassName: 'p-0',
            footer: <></>
        }));
    };

    const handleSubmitInactiveModal = async ({ reason }, cb) => {
        const res = await flowResult(updateStatus(code, {
            status: SHOP_STATUS_TYPE.Inactive,
            reason
        }));
        if (res?.ok) {
            cb && cb();
            fetchData();
            toastify('success', t('messages.update_success'));
        }
    };

    return (
        <div className='bg-white mt-4 rounded p-4 flex justify-between items-center sticky z-10 bottom-0 left-0'>
            {
                shopInfo && (shopInfo as GetGeneralInfoResponse).status === SHOP_STATUS_TYPE.WaitApprove &&
                (
                    <div className='flex items-center gap-6'>
                        <ActionFooterButton
                            text={t('button.reject_shop')}
                            icon={<XIcon className='w-5 h-5 text-delete-action' />}
                            disabled={form.formState.isDirty}
                            onClick={handleClickDenied}
                            buttonClassname='border border-delete-action text-delete-action'
                        />
                        <ActionFooterButton
                            text={t('button.approve_shop')}
                            icon={<CheckIcon className='w-5 h-5 text-accept-action' />}
                            disabled={form.formState.isDirty}
                            onClick={handleClickAccept}
                            buttonClassname='border border-accept-action text-accept-action'
                        />
                    </div>
                )
            }
            <div>
                {
                    shopInfo && (shopInfo as GetGeneralInfoResponse).status === SHOP_STATUS_TYPE.Active &&
                    (
                        <ActionFooterButton
                            text={t('button.inactive_shop')}
                            icon={<BanIcon className='w-5 h-5 text-delete-action' />}
                            disabled={form.formState.isDirty}
                            onClick={handleClickInactive}
                            buttonClassname='border border-delete-action text-delete-action'
                        />
                    )
                }
                {
                    shopInfo && (shopInfo as GetGeneralInfoResponse).status === SHOP_STATUS_TYPE.Inactive &&
                    (
                        <ActionFooterButton
                            text={t('button.active_shop')}
                            icon={<CircleCheckIcon className='w-5 h-5 text-accept-action' />}
                            disabled={form.formState.isDirty}
                            onClick={handleClickActive}
                            buttonClassname='border border-accept-action text-accept-action'
                        />
                    )
                }
                {
                    shopInfo && (shopInfo as GetGeneralInfoResponse).status === SHOP_STATUS_TYPE.NotCompletedInfo &&
                    (
                        <div className='flex items-center gap-6'>
                            <ActionFooterButton
                                text={t('button.inactive_shop')}
                                icon={<BanIcon className='w-5 h-5 text-delete-action' />}
                                disabled={form.formState.isDirty}
                                onClick={handleClickInactive}
                                buttonClassname='border border-delete-action text-delete-action'
                            />
                            <ActionFooterButton
                                text={t('button.active_shop')}
                                icon={<CircleCheckIcon className='w-5 h-5 text-accept-action' />}
                                disabled={form.formState.isDirty}
                                onClick={handleClickActive}
                                buttonClassname='border border-accept-action text-accept-action'
                            />
                        </div>
                    )
                }
            </div>
            <div className='flex items-center gap-6'>
                <Button type='button' onClick={handleClickCancel} className='min-w-[7rem]' variant='outline'>{t('button.cancel')}</Button>
                <Button type='submit' disabled={isSubmitDisabled} onClick={onSubmit} className='min-w-[7rem]' variant='default'>{t('button.save')}</Button>
            </div>
        </div>
    );
});

interface ActionFooterButtonProps {
    text: string,
    icon: ReactElement,
    disabled: boolean,
    onClick: () => void,
    buttonClassname: string
}

const ActionFooterButton = ({ text, icon, disabled, onClick, buttonClassname }: ActionFooterButtonProps) => {
    // hooks
    const { t } = useTranslation();

    return (
        <>
            {
                disabled ?
                    (
                        <Tooltip
                            align='start'
                            content={t('sentences.alert_save_before_submit')}
                            triggerType='button'
                            triggerClassName='cursor-default'
                            triggerAsChild={true}
                        >
                            <Button
                                type='button'
                                disabled
                                className={classNames('flex items-center justify-center px-4 py-5 bg-white min-w-[7rem] gap-2', buttonClassname)}
                            >
                                {icon}
                                {text}
                            </Button>
                        </Tooltip>
                    ) :
                    (
                        <Button
                            type='button'
                            onClick={onClick}
                            className={classNames('flex items-center justify-center px-4 py-5 bg-white min-w-[7rem] gap-2', buttonClassname)}
                        >
                            {icon}
                            {text}
                        </Button>
                    )
            }
        </>
    );
};
